import * as React from 'react';

import { Text, TextLink } from '@lightspeed/flame/Text';
import { useDispatch, useSelector } from 'react-redux';

import { Alert } from '@lightspeed/flame/Alert';
import { Autocomplete } from '@lightspeed/flame/Autocomplete';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { ModalHeader as FlameModalHeader } from '@lightspeed/flame/Modal';
import { IconDanger } from '@lightspeed/flame/Icon/Danger';
import { addShopAccess, clearShopAccess } from 'pages/store/actions/employeeShops';
import { getStore } from 'pages/store/selectors/employeeShops';
import styled from '@emotion/styled';
import { theme } from '@lightspeed/flame/Core/themes/flame';
import { ShopAutoComplete } from '../../types/shops';

const StyledTextLink = styled(TextLink)`
  text-decoration: underline;
`;

const ModalHeader: React.FC = () => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = React.useState('');

  const { error, shops } = useSelector(getStore);
  const allowedShops = shops.filter(shop => !shop.hasAccess);
  const employeeShops = shops.filter(shop => shop.hasAccess);

  const handleInputChange = React.useCallback((value, meta) => {
    const newInputValue = ['set-value', 'menu-close'].includes(meta.action) ? '\r' : value;
    setInputValue(newInputValue);
    return newInputValue;
  }, []);

  const handleAutocompleteChange = (newShop: ShopAutoComplete) =>
    dispatch(addShopAccess(newShop.value));

  const handleRemoveAllClick = () => dispatch(clearShopAccess());

  const totalEmployeeShops = employeeShops.length;

  const translate = (text: string) => {
    // Cannot use window.Localize?.translate because of return type mismatch
    if (window.Localize) {
      return window.Localize.translate(text);
    }
    return text;
  };

  return (
    <>
      <FlameModalHeader>Location access</FlameModalHeader>
      <Box p={2}>
        {error && (
          <Alert
            data-testid="unable-to-save"
            icon={<IconDanger color="maple-500" />}
            marginBottom={3}
            py={3}
            type="danger"
          >
            Unable to save employee&apos;s location access. Try again.
          </Alert>
        )}
        <Autocomplete
          className="notranslate"
          data-testid="shops-search-field"
          inputId="shop-input"
          inputValue={inputValue}
          name="shop-input"
          noOptionsMessage={() => translate('No options')}
          onChange={handleAutocompleteChange}
          onInputChange={handleInputChange}
          options={allowedShops.map(shop => ({ value: shop.id, label: shop.name }))}
          placeholder={translate('Select a location')}
        />
      </Box>
      <Divider />
      {!!totalEmployeeShops && (
        <Box>
          <Flex justifyContent="space-between" alignItems="center" py={2} px={3}>
            <Text size="small" color={theme.colors['gray-600']} data-testid="shop-counter-modal">
              <var>{totalEmployeeShops}</var> of <var>{shops.length}</var> locations
            </Text>
            <StyledTextLink
              color={theme.colors['gray-600']}
              onClick={handleRemoveAllClick}
              size="small"
            >
              Remove all locations
            </StyledTextLink>
          </Flex>
          <Divider />
        </Box>
      )}
    </>
  );
};

export default ModalHeader;
